import styles from './index.module.css'

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import Tooltip from 'components/ui/tooltip'
import useEventListener from 'hooks/use-event-listener'
type Props = {
  children: React.ReactNode
  maxLines: number
}

/**
 * A component that truncates text to a specified number of lines and shows full content on hover
 * @param children - The content to be truncated
 * @param maxLines - The maximum number of lines to display before truncation
 * @returns A truncated text component with hover-to-expand functionality
 */
const TruncateOnHover = ({
  children,
  maxLines,
}: Props): React.ReactElement<'div'> => {
  const parentContainerRef = useRef(null)
  const [maxWidth, setMaxWidth] = useState(0)
  const [rootStyles, setRootStyles] = useState({
    '--lineClamp': maxLines.toString(),
  })
  useLayoutEffect(() => {
    setRootStyles({
      '--lineClamp': maxLines.toString(),
    })
  }, [maxLines])
  const updateMaxWidth = useCallback(() => {
    if (parentContainerRef.current) {
      const gridItemParent = parentContainerRef.current.offsetWidth

      if (gridItemParent !== maxWidth) {
        setMaxWidth(gridItemParent)
      }
    }
  }, [])
  useEventListener('resize', updateMaxWidth)
  useEffect(() => {
    updateMaxWidth()
    const root = document.documentElement

    if (root) {
      for (const [key, value] of Object.entries(rootStyles)) {
        root.style.setProperty(key, String(value))
      }
    }
  }, [updateMaxWidth, rootStyles])
  return (
    <div ref={parentContainerRef}>
      <Tooltip
        maxWidth={`${maxWidth}px`}
        marginTop={`${maxLines * -1.475}em`}
        position="bottom"
        content={
          <div className={`${styles.truncate} ${styles.truncateExpanded}`}>
            {children}
          </div>
        }
      >
        <div className={`${styles.truncate} ${styles.truncateLineClamp} `}>
          {children}
        </div>
      </Tooltip>
    </div>
  )
}

export default TruncateOnHover
